import { Alert, Button, Form, Input, Result, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, unwrap, type Role } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { AuthLayout } from "@/components/AuthLayout";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { useI18n } from "@/i18n";

type Values = { name?: string; password: string };

/** "Terima undangan" (App Flow §10). Works whether or not the address has an account. */
export function AcceptInvite() {
  const { t } = useI18n();
  const { token = "" } = useParams();
  const { signIn, signOut, status } = useAuth();
  const navigate = useNavigate();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => unwrap(api.POST("/v1/auth/invitations/inspect", { body: { token } })),
    retry: false,
  });

  if (preview.isPending) return <FullScreenLoader />;
  if (preview.isError) {
    return (
      <AuthLayout title={t("auth.invite.invalidTitle")}>
        <Result status="warning" subTitle={t("auth.invite.invalidBody")} extra={<Link to="/login">{t("auth.register.login")}</Link>} />
      </AuthLayout>
    );
  }

  const inv = preview.data;
  const submit = async (values: Values) => {
    setSubmitting(true);
    setError(null);
    try {
      // Someone else may be signed in on this browser; the invitation is for
      // whoever owns the invited address, so start clean.
      if (status === "authenticated") await signOut();
      const session = await unwrap(api.POST("/v1/auth/invitations/accept", { body: { token, ...values } }));
      await signIn(session);
      navigate("/app", { replace: true });
    } catch (err) {
      if (!applyFieldErrors(form, err)) setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.invite.title", { workspace: inv.workspaceName })}
      subtitle={t("auth.invite.body", { role: t(`role.${inv.role as Role}`), email: inv.email })}
    >
      <Typography.Paragraph>{inv.accountExists ? t("auth.invite.existingAccount") : t("auth.invite.newAccount")}</Typography.Paragraph>
      {error !== null && <Alert type="error" showIcon title={errorText(error)} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} disabled={submitting}>
        {!inv.accountExists && (
          <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
            <Input autoComplete="name" autoFocus />
          </Form.Item>
        )}
        <Form.Item
          name="password"
          label={t("common.password")}
          extra={inv.accountExists ? undefined : t("field.password_hint")}
          rules={
            inv.accountExists
              ? [{ required: true, message: t("field.required") }]
              : [{ required: true, min: 10, message: t("field.password_hint") }, { max: 128, message: t("field.too_long") }]
          }
        >
          <Input.Password autoComplete={inv.accountExists ? "current-password" : "new-password"} autoFocus={inv.accountExists} />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={submitting}>
          {t("auth.invite.submit")}
        </Button>
      </Form>
    </AuthLayout>
  );
}
